import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input, Button, LoadingSpinner } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import elAlssonLogo from '../../assets/images/el-alsson-logo.png';
import './LoginPage.css';

/**
 * LoginPage — single unified card on a full-screen aurora background.
 *
 * No external Card wrapper, no AuthLayout header — the page owns its full
 * visual treatment so it lays out predictably without nested padding.
 *
 *   1. <form onSubmit={handleSubmit} noValidate>
 *   2. handleSubmit calls e.preventDefault() FIRST, then validates against
 *      live React state.
 *   3. login() awaits the API; redirect happens via useEffect on isAuthenticated.
 */
const LoginPage = () => {
  const { login, loading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/dashboard';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const validate = () => {
    const errs = {};
    if (!username.trim()) errs.username = 'Username is required';
    if (!password) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      await login({ username: username.trim(), password });
    } catch {
      // error message lives in auth context's `error` and is shown below
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-brand">
            <img
              src={elAlssonLogo}
              alt="El Alsson School"
              className="login-logo"
            />
            <h1 className="login-brand-title">El Alsson School</h1>
            <p className="login-brand-sub">Accounting System</p>
          </div>

          <div className="login-divider" aria-hidden="true"></div>

          <div className="login-form-section">
            <h2 className="login-heading">Welcome back</h2>
            <p className="login-subtitle">
              Sign in to continue to the accounting system.
            </p>

            <form onSubmit={handleSubmit} noValidate className="login-form">
              <Input
                type="text"
                name="username"
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={fieldErrors.username}
                placeholder="Enter your username"
                autoComplete="username"
                autoFocus
                icon="fas fa-user"
              />

              <Input
                type="password"
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={fieldErrors.password}
                placeholder="Enter your password"
                autoComplete="current-password"
                icon="fas fa-lock"
              />

              {error && (
                <div className="login-error" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? <LoadingSpinner size="small" variant="button" /> : 'Sign In'}
              </Button>
            </form>
          </div>

          <div className="login-help">
            <p className="login-help__label">Need help?</p>
            <a
              href="https://support.finance.alsson.app"
              target="_blank"
              rel="noopener noreferrer"
              className="login-help__btn"
            >
              <i className="fas fa-ticket-alt" aria-hidden="true"></i>
              Create Support Ticket
            </a>
            <p className="login-help__or">
              or contact{' '}
              <a href="mailto:support.finance@alsson.app">support.finance@alsson.app</a>
            </p>
          </div>
        </div>

        <p className="login-copyright">
          &copy; 2025 El Alsson British &amp; American International School.
          All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
